import React, { useEffect, useState, useMemo, useCallback } from "react";
import { api, formatDateBR, todayISO } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Printer, ClipboardCheck, Search } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const TIPO_VEICULO_OPTIONS = [
    { value: "onibus", label: "Ônibus" },
    { value: "caminhonete", label: "Caminhonete" },
    { value: "carro", label: "Carro" },
    { value: "van", label: "Van" },
    { value: "caminhao", label: "Caminhão" },
    { value: "outro", label: "Outro" },
];
const TIPO_LABELS = Object.fromEntries(TIPO_VEICULO_OPTIONS.map((t) => [t.value, t.label]));

const EMPREENDIMENTOS = [
    { value: "km117", label: "Empreendimento KM 117" },
    { value: "rio_cupari", label: "Rio Cupari" },
    { value: "sede", label: "Sede COOMFLONA" },
    { value: "setor_social", label: "Setor Social" },
    { value: "nao_madeireiro", label: "Não Madeireiro" },
    { value: "outro", label: "Outro" },
];
const EMPR_LABELS = Object.fromEntries(EMPREENDIMENTOS.map((e) => [e.value, e.label]));

const STATUS_OPTIONS = [
    { value: "autorizada", label: "Autorizada", color: "bg-blue-100 text-blue-800 border-blue-300" },
    { value: "em_execucao", label: "Em execução", color: "bg-yellow-100 text-yellow-800 border-yellow-300" },
    { value: "concluida", label: "Concluída", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    { value: "cancelada", label: "Cancelada", color: "bg-red-100 text-red-800 border-red-300" },
];
const STATUS_MAP = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, s]));

const empty = {
    tipo_veiculo: "carro",
    veiculo_placa: "",
    veiculo_modelo: "",
    motorista_nome: "",
    destino: "",
    finalidade: "",
    data_servico: todayISO(),
    hora_saida: "",
    hora_retorno: "",
    autorizada_por: "Nita",
    solicitante: "",
    empreendimento: "sede",
    status: "autorizada",
    observacoes: "",
};

export default function ServiceOrders() {
    const { isAdmin } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [editingId, setEditingId] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [filter, setFilter] = useState("todos");
    const [search, setSearch] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        const params = {};
        if (filter !== "todos") params.status = filter;
        const res = await api.get("/service-orders", { params });
        setOrders(res.data || []);
        setLoading(false);
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        if (!search) return orders;
        const q = search.toLowerCase();
        return orders.filter((o) =>
            (o.destino || "").toLowerCase().includes(q) ||
            (o.motorista_nome || "").toLowerCase().includes(q) ||
            (o.veiculo_placa || "").toLowerCase().includes(q) ||
            String(o.numero || "").includes(q)
        );
    }, [orders, search]);

    const openNew = () => { setForm(empty); setEditingId(null); setOpen(true); };
    const openEdit = (o) => {
        setForm({
            tipo_veiculo: o.tipo_veiculo, veiculo_placa: o.veiculo_placa || "",
            veiculo_modelo: o.veiculo_modelo || "", motorista_nome: o.motorista_nome || "",
            destino: o.destino, finalidade: o.finalidade,
            data_servico: o.data_servico, hora_saida: o.hora_saida || "", hora_retorno: o.hora_retorno || "",
            autorizada_por: o.autorizada_por || "Nita", solicitante: o.solicitante || "",
            empreendimento: o.empreendimento || "sede", status: o.status,
            observacoes: o.observacoes || "",
        });
        setEditingId(o.id);
        setOpen(true);
    };

    const save = async () => {
        if (!form.destino.trim() || !form.finalidade.trim() || !form.data_servico) {
            toast.error("Preencha destino, finalidade e data");
            return;
        }
        try {
            if (editingId) {
                await api.put(`/service-orders/${editingId}`, form);
                toast.success("Ordem de serviço atualizada!");
            } else {
                await api.post("/service-orders", form);
                toast.success("Ordem de serviço criada!");
            }
            setOpen(false);
            load();
        } catch {
            toast.error("Erro ao salvar");
        }
    };

    const del = async (id) => {
        try {
            await api.delete(`/service-orders/${id}`);
            toast.success("Removida");
            load();
        } catch {
            toast.error("Erro ao remover");
        } finally {
            setConfirmDelete(null);
        }
    };

    const changeStatus = async (o, newStatus) => {
        try {
            await api.put(`/service-orders/${o.id}`, { ...o, status: newStatus });
            toast.success("Status atualizado");
            load();
        } catch {
            toast.error("Erro ao atualizar status");
        }
    };

    return (
        <div className="space-y-6" data-testid="service-orders-page">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="text-3xl font-extrabold text-foreground flex items-center gap-3">
                        <ClipboardCheck className="w-7 h-7 text-primary" /> Ordens de Serviço
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">Autorizações de serviços de transporte (Nita)</p>
                </div>
                <Button onClick={openNew} className="gap-2" data-testid="btn-new-os">
                    <Plus className="w-4 h-4" /> Nova Ordem
                </Button>
            </div>

            <Card>
                <CardContent className="p-4 flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input className="pl-9" placeholder="Buscar por nº OS, destino, motorista ou placa" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="input-search-os" />
                    </div>
                    <Select value={filter} onValueChange={setFilter}>
                        <SelectTrigger className="w-56" data-testid="select-filter-os"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="todos">Todas</SelectItem>
                            {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </CardContent>
            </Card>

            <Card>
                <CardContent className="p-5">
                    {loading ? (
                        <div className="text-center py-8 text-muted-foreground">Carregando...</div>
                    ) : filtered.length === 0 ? (
                        <div className="py-14 text-center border-2 border-dashed border-border rounded-xl">
                            <ClipboardCheck className="w-10 h-10 mx-auto text-muted-foreground" />
                            <div className="mt-3 font-semibold">Nenhuma OS cadastrada</div>
                            <div className="text-sm text-muted-foreground mt-1">Clique em &quot;Nova Ordem&quot; para começar</div>
                        </div>
                    ) : (
                        <div className="space-y-3" data-testid="os-list">
                            {filtered.map((o) => {
                                const st = STATUS_MAP[o.status];
                                return (
                                    <div key={o.id} className="rounded-lg border border-border p-4 hover:shadow-sm transition">
                                        <div className="flex items-start justify-between gap-4 flex-wrap">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-mono font-bold text-lg text-primary">OS #{String(o.numero || 0).padStart(4, "0")}</span>
                                                    <Badge variant="outline" className={`${st?.color} text-[10px] uppercase font-bold`}>{st?.label || o.status}</Badge>
                                                    <span className="text-xs text-muted-foreground">{formatDateBR(o.data_servico)}</span>
                                                </div>
                                                <div className="mt-2 text-sm">
                                                    <div><span className="text-muted-foreground">Destino:</span> <strong>{o.destino}</strong> — {TIPO_LABELS[o.tipo_veiculo]}</div>
                                                    <div className="text-xs text-muted-foreground mt-0.5">{o.finalidade}</div>
                                                </div>
                                                <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-1 text-sm">
                                                    {o.motorista_nome && <div><span className="text-muted-foreground">Motorista:</span> <strong>{o.motorista_nome}</strong></div>}
                                                    {o.veiculo_placa && <div><span className="text-muted-foreground">Placa:</span> <strong className="font-mono">{o.veiculo_placa}</strong></div>}
                                                    <div><span className="text-muted-foreground">Empreendimento:</span> <strong>{EMPR_LABELS[o.empreendimento]}</strong></div>
                                                </div>
                                                <div className="mt-1 text-xs">
                                                    <span className="text-muted-foreground">Autorizada por:</span> <strong className="text-primary">{o.autorizada_por || "—"}</strong>
                                                </div>
                                            </div>
                                            <div className="flex gap-1 flex-wrap">
                                                <Button variant="outline" size="sm" onClick={() => window.open(`/os/${o.id}/imprimir`, "_blank")} title="Imprimir" data-testid={`btn-print-os-${o.id}`}>
                                                    <Printer className="w-4 h-4" />
                                                </Button>
                                                <Button variant="outline" size="sm" onClick={() => openEdit(o)} data-testid={`btn-edit-os-${o.id}`}>
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                {isAdmin && (
                                                    <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(o)} data-testid={`btn-delete-os-${o.id}`}>
                                                        <Trash2 className="w-4 h-4 text-destructive" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="mt-3 flex gap-1 flex-wrap">
                                            {STATUS_OPTIONS.filter((s) => s.value !== o.status).map((s) => (
                                                <Button key={s.value} size="sm" variant="ghost" onClick={() => changeStatus(o, s.value)} className={`text-[11px] ${s.color} border`} data-testid={`btn-status-${s.value}-${o.id}`}>
                                                    → {s.label}
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="os-dialog">
                    <DialogHeader>
                        <DialogTitle>{editingId ? "Editar" : "Nova"} Ordem de Serviço</DialogTitle>
                        <DialogDescription>Autorização de serviço de transporte</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Tipo de veículo *</Label>
                                <Select value={form.tipo_veiculo} onValueChange={(v) => setForm({ ...form, tipo_veiculo: v })}>
                                    <SelectTrigger data-testid="select-os-tipo"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {TIPO_VEICULO_OPTIONS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Empreendimento *</Label>
                                <Select value={form.empreendimento} onValueChange={(v) => setForm({ ...form, empreendimento: v })}>
                                    <SelectTrigger data-testid="select-os-empr"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {EMPREENDIMENTOS.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Placa</Label>
                                <Input value={form.veiculo_placa} onChange={(e) => setForm({ ...form, veiculo_placa: e.target.value.toUpperCase() })} placeholder="ABC-1234" data-testid="input-os-placa" />
                            </div>
                            <div>
                                <Label>Modelo do veículo</Label>
                                <Input value={form.veiculo_modelo} onChange={(e) => setForm({ ...form, veiculo_modelo: e.target.value })} placeholder="Ex: Hilux, Sprinter" data-testid="input-os-modelo" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Motorista</Label>
                                <Input value={form.motorista_nome} onChange={(e) => setForm({ ...form, motorista_nome: e.target.value })} data-testid="input-os-motorista" />
                            </div>
                            <div>
                                <Label>Solicitante</Label>
                                <Input value={form.solicitante} onChange={(e) => setForm({ ...form, solicitante: e.target.value })} placeholder="Setor / pessoa" data-testid="input-os-solicitante" />
                            </div>
                        </div>
                        <div>
                            <Label>Destino *</Label>
                            <Input value={form.destino} onChange={(e) => setForm({ ...form, destino: e.target.value })} placeholder="Ex: Santarém - PA" data-testid="input-os-destino" />
                        </div>
                        <div>
                            <Label>Finalidade / descrição *</Label>
                            <Textarea value={form.finalidade} onChange={(e) => setForm({ ...form, finalidade: e.target.value })} placeholder="Descreva o serviço a ser executado" data-testid="input-os-finalidade" />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <Label>Data *</Label>
                                <Input type="date" value={form.data_servico} onChange={(e) => setForm({ ...form, data_servico: e.target.value })} data-testid="input-os-data" />
                            </div>
                            <div>
                                <Label>Hora saída</Label>
                                <Input type="time" value={form.hora_saida} onChange={(e) => setForm({ ...form, hora_saida: e.target.value })} data-testid="input-os-saida" />
                            </div>
                            <div>
                                <Label>Hora retorno</Label>
                                <Input type="time" value={form.hora_retorno} onChange={(e) => setForm({ ...form, hora_retorno: e.target.value })} data-testid="input-os-retorno" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Autorizada por</Label>
                                <Input value={form.autorizada_por} onChange={(e) => setForm({ ...form, autorizada_por: e.target.value })} data-testid="input-os-autorizada" />
                            </div>
                            <div>
                                <Label>Status</Label>
                                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                    <SelectTrigger data-testid="select-os-status"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label>Observações</Label>
                            <Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} data-testid="input-os-obs" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={save} data-testid="btn-save-os">Salvar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remover OS?</AlertDialogTitle>
                        <AlertDialogDescription>OS #{confirmDelete?.numero} — {confirmDelete?.destino}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => del(confirmDelete.id)}>Remover</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
